import { useApolloClient } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { ProductCatalogProduct } from 'src/modules/homeownerApp/components/Palette/types';
import type {
  ProductCatalogCustomizeProductsQuery,
  ProductCatalogCustomizeProductsQueryVariables,
} from 'src/modules/homeownerApp/graphql/generated';
import { ProductCatalogCustomizeProductsDocument } from 'src/modules/homeownerApp/graphql/generated';

/**
 * Set.prototype.difference does not have broad support yet. This gets the job
 * done for now.
 *
 * Returns all strings of a that are not present in b
 */
const difference = (a: string[], b: string[]) => {
  const bMap = b.reduce((accum, str) => {
    accum[str] = true;
    return accum;
  }, {} as Record<string, boolean>);

  return a.filter(str => !bMap[str]);
};

/**
 * Given a list of productCatalogCategoryIds, executes multiple
 * ProductCatalogCustomizeProduct queries and returns the combined list of products.
 */
export const useMultipleCategoryProductCatalogProducts = ({
  genAiOrgId,
  productCatalogCategoryIds,
  skip,
}: {
  genAiOrgId?: number;
  productCatalogCategoryIds: string[];
  skip?: boolean;
}): {
  products: ProductCatalogProduct[] | undefined;
  loading: boolean;
} => {
  const client = useApolloClient();
  const [productsByCategoryId, setProductsByCategoryId] = useState<
    Record<string, ProductCatalogProduct[]>
  >({});
  const lastQueriedIdsRef = useRef<string[]>([]);
  const lastQueriedGenAiOrgIdRef = useRef<number | undefined>();

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (skip) {
      return;
    }

    const lastQueriedIds = lastQueriedIdsRef.current;
    const genAiOrgIdMatches = lastQueriedGenAiOrgIdRef.current === genAiOrgId;

    // If genAiOrgId has changed, then all lastQueriedIds need to be
    // considered removed as the saved result is for a different genAiOrgId
    const idsRemoved = genAiOrgIdMatches
      ? difference(lastQueriedIds, productCatalogCategoryIds)
      : lastQueriedIds;

    if (
      lastQueriedIds.length === productCatalogCategoryIds.length &&
      idsRemoved.length === 0
    ) {
      return;
    }

    if (idsRemoved.length > 0) {
      setProductsByCategoryId(prevState =>
        Object.keys(prevState)
          .filter(id => !idsRemoved.includes(id))
          .reduce((accum, id) => {
            accum[id] = prevState[id];
            return accum;
          }, {} as Record<string, ProductCatalogProduct[]>),
      );
    }

    // If genAiOrgId has changed, then all productCatalogCategoryIds need to
    // be considered added to ensure all queries are re-run with the correct
    // genAiOrgId filter.
    const idsAdded = genAiOrgIdMatches
      ? difference(productCatalogCategoryIds, lastQueriedIds)
      : productCatalogCategoryIds;

    if (
      idsAdded.length > 0 &&
      (lastQueriedIds.length === 0 ||
        lastQueriedIds.length === idsRemoved.length)
    ) {
      setLoading(true);
    }

    idsAdded.forEach(productCatalogCategoryId => {
      const queryProducts = async () => {
        const result = await client.query<
          ProductCatalogCustomizeProductsQuery,
          ProductCatalogCustomizeProductsQueryVariables
        >({
          query: ProductCatalogCustomizeProductsDocument,
          variables: {
            genAiOrgId: genAiOrgId ? String(genAiOrgId) : undefined,
            productCatalogCategoryId,
          },
        });

        const categoryProducts = result.data?.productCatalogProducts.nodes;

        if (categoryProducts) {
          setProductsByCategoryId(prevState => ({
            ...prevState,
            [productCatalogCategoryId]: categoryProducts,
          }));
        }
        // loading is set to false as soon as at least one category result is available
        setLoading(false);
      };

      void queryProducts();
    });

    lastQueriedIdsRef.current = productCatalogCategoryIds;
    lastQueriedGenAiOrgIdRef.current = genAiOrgId;
  }, [client, skip, genAiOrgId, productCatalogCategoryIds]);

  return useMemo(() => {
    // Products can belong to multiple categories. To ensure the list of products
    // only contains unique entries, all products in all categories are reduced
    // to a map of product id to product. The resulting map object has no duplicates
    const uniqueProductsById = Object.values(productsByCategoryId).reduce(
      (accum, categoryProducts) => {
        categoryProducts.forEach(product => {
          if (!product) {
            return;
          }
          accum[product.id] = product;
        });
        return accum;
      },
      {} as Record<string, ProductCatalogProduct>,
    );

    const products = Object.values(uniqueProductsById);

    return {
      products:
        lastQueriedIdsRef.current.length && !loading ? products : undefined,
      loading,
    };
  }, [productsByCategoryId, loading]);
};
